import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Notification } from "@rio-cloud/rio-uikit";
import { CheckpointStop, IOnGoingTrip, IUrgentAlert } from "@types";

import { ICoordinates, IRoutePoint, ISandboxMarker, getBoundingBox } from "src/components/Map";
import { useDebounce } from "src/hooks/useDebounce";
import { AuthContext } from "src/providers/AuthProvider";
import { ConfigPaginator } from "src/components/PaginatorSize";
import { WebSocketContext } from "src/providers/WebsocketProvider";
import { getCheckpointDetailByIdV2 } from "src/services/checkpoint";
import { fetchDetail, fetchUrltripShare } from "src/services/routes";
import {
  acceptTrip,
  fetchDetailsTripOnGoing,
  fetchTripsListOnGoing,
  fetchUnplannedStops,
  fetchUrgentAlerts,
  finishTripManually,
  getSuggestedRoute,
} from "src/services/trips";
import { Result } from "src/types/routes";
import { decode } from "src/utils/flexiblePolyline";
import { config } from "../../../config";
import { mountOnGoingDashboard } from "./mountOnGoingDashboard";
import { Position } from "@rio-cloud/rio-uikit/mapTypes";
import { useHandleRoutes } from "@hooks";

const positionDefault = {
  lat: -23.5475,
  lng: -46.63611,
};

const PAGE_SIZE = 10;

export const useOnGoingTripsController = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState<Array<IOnGoingTrip>>([]);
  const { followers, rioDrivers } = useContext(AuthContext);
  const [searchValue, setSearchValue] = useState("");
  const [sort, setSort] = useState<{ [name: string]: string }>({ dateStarted: "desc" });
  const [fieldSort, setFieldSort] = useState("dateStarted");
  const [selected, setSelected] = useState<IOnGoingTrip | null>();
  const [details, setDetails] = useState<Result | null>();
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [totalTrips, setTotalTrips] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE);
  const [isPolyline, setIsPolyline] = useState(false);
  const [zoom, setZoom] = useState(10);
  const ctx = useContext(WebSocketContext);
  const { handleGenerateMarker, handleSimpleLocation, handleRoutes } = useHandleRoutes();

  const [dashboard, setDashboard] = useState({
    ongoingRoute: 0,
    outOfRoute: 0,
    overdueRoute: 0,
    earlyRoute: 0,
    notStarted: 0,
  });
  const [filterByType, setFilterByType] = useState<string>();

  const [position, setPosition] = useState<ICoordinates | undefined>(positionDefault);
  const [boundingBox, setBoundingBox] = useState<
    { top: number; left: number; right: number; bottom: number } | undefined
  >();
  const [segments, setSegments] = useState<IRoutePoint[]>([]);
  const [markers, setMarkers] = useState<ISandboxMarker[]>([]);
  const [view, setView] = useState<"DEFAULT" | "DETAILS">("DEFAULT");
  const [closed, setClosed] = useState(true);
  const [stops, setStops] = useState<Array<{ note: string; label: string; checkpointId?: string; order?: number }>>([]);
  const [currencySegments, setCurrencySegments] = useState<IRoutePoint[]>([]);
  const [suggestedSegments, setSuggestedSegments] = useState<IRoutePoint[]>([]);
  const [suggestedRouteData, setSuggestedRouteData] = useState<any>(null);
  const [statusTripNow, setStatusTripNow] = useState("");
  const [statusRouteNow, setStatusRouteNow] = useState("");
  const [progressNow, setProgressNow] = useState<string>();
  const [markerNow, setMarkerNow] = useState<ISandboxMarker>();
  const [locationNow, setLocationNow] = useState("");
  const debouncedSearch = useDebounce(searchValue, 400);
  const [checkpoint, setCheckpoint] = useState<CheckpointStop>();
  const [openStages, setOpenStages] = useState(false);
  const [suggestedRoutes, setSuggestedRoutes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingAcceptTrip, setIsLoadingAcceptTrip] = useState(false);
  const [urgentAlerts, setUrgentAlerts] = useState<IUrgentAlert[]>([]);
  const [urgentAlertMarkers, setUrgentAlertMarkers] = useState<ISandboxMarker[]>([]);

  // Refs para controle de cache e prevenção de loops infinitos
  const suggestedRoutesCache = useRef<any[]>([]);
  const isFetchingSuggestionsRef = useRef(false);
  const lastFetchedRoutesRef = useRef<string>("");

  // Refs para controle do fluxo de detalhes da viagem
  const selectedTripIdRef = useRef<string | null>(null);
  const selectedRouteIdRef = useRef<string | null>(null);
  const isUpdatingDetailsRef = useRef(false);
  const isFetchingRouteDetailsRef = useRef(false);
  const detailsIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleClearSuggest = useCallback(async () => {
    setSuggestedRoutes([]);
    setSuggestedRouteData(undefined);
    setSuggestedSegments([]);
    // Limpa também o cache do ref para garantir consistência
    suggestedRoutesCache.current = [];
    lastFetchedRoutesRef.current = "";
  }, []);

  useEffect(() => {
    handleClearSuggest();
  }, [handleClearSuggest]);

  // NÃO REMOVER, WEBSOCKET DESABILITADO.
  // useEffect(() => {
  //   if (ctx?.lastMessage?.data && ctx?.lastMessage?.data.includes('TRIP_LOCATION_UPDATE')) {
  //     setUpdatedLengh(updatedLenght + 1);
  //     fetchAllTrips();
  //   }
  // }, [ctx])
  // useEffect(() => {
  //   if (updatedLenght > 0 && selected) {
  //     Update();
  //   }
  // }, [updatedLenght])

  const fetchSuggestedRoutes = useCallback(async () => {
    // Evita chamadas simultâneas
    if (isFetchingSuggestionsRef.current) {
      return;
    }

    // Evita buscar se não há rotas
    if (!routes || routes.length === 0) {
      return;
    }

    // Verifica se as rotas realmente mudaram (por IDs)
    const currentRoutesKey = routes
      .map((r) => r.tripId)
      .sort()
      .join(",");
    if (currentRoutesKey === lastFetchedRoutesRef.current) {
      return;
    }

    isFetchingSuggestionsRef.current = true;
    lastFetchedRoutesRef.current = currentRoutesKey;

    try {
      const newSuggestedRoutes: any[] = await Promise.all(
        routes.map(async (route) => {
          try {
            // Usa o cache do ref em vez do estado
            const cachedData = suggestedRoutesCache.current.find((f) => f.tripId === route.tripId && f.data);
            if (cachedData) {
              return cachedData;
            }

            const suggestedRouteToUse = await getSuggestedRoute(route.tripId);
            return {
              tripId: route.tripId,
              data: suggestedRouteToUse.length > 0 ? suggestedRouteToUse : null,
            };
          } catch (error) {
            return {
              tripId: route.tripId,
              data: null,
            };
          }
        })
      );

      // Atualiza o cache e o estado
      suggestedRoutesCache.current = newSuggestedRoutes;
      setSuggestedRoutes(newSuggestedRoutes);
    } finally {
      isFetchingSuggestionsRef.current = false;
    }
  }, [routes]); // Removido suggestedRoutes das dependências - usa ref para cache

  useEffect(() => {
    fetchSuggestedRoutes();
  }, [fetchSuggestedRoutes]);

  const fetchAllTrips = useCallback(
    async (signal?: AbortSignal, search = debouncedSearch) => {
      try {
        let all: IOnGoingTrip[] = [];

        const res = await fetchTripsListOnGoing(
          1,
          size,
          search.length > 0 ? search : "",
          sort[fieldSort],
          fieldSort,
          filterByType,
          { signal }
        );
        if (res.data.result) {
          all = res.data?.result?.routes || [];
          const pagesTemp = res.data?.result?.pages || 0;
          const dashboardData = mountOnGoingDashboard(res.data?.result);
          const totalCount = res.data?.result?.allTrips || 0;

          setRoutes(all);
          setPages(pagesTemp);
          setTotalTrips(totalCount);
          setDashboard(dashboardData);
        }
        setIsLoading(false);
        setIsLoadingMore(false);
      } catch (error) {
        if ((error as any).name !== "CanceledError") Notification.error("Não foi possível buscar as rotas.");
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [debouncedSearch, fieldSort, filterByType, size, sort]
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    fetchAllTrips(signal);

    return () => {
      controller.abort();
    };
  }, [fetchAllTrips]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchAllTrips();
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchAllTrips]);

  const loadCheckpointDetails = async (checkpointId: string) => {
    try {
      const response = await getCheckpointDetailByIdV2(checkpointId);
      setCheckpoint(response?.data?.result);
      setClosed(true);
      setOpenStages(true);
    } catch (err) {
      console.error("Erro ao buscar detalhes da viagem:", err);
    }
  };

  // fetchAllTrips moved above (useCallback) to satisfy hooks deps

  const processSuggestedRoute = useCallback((routes: any[]): IRoutePoint[] => {
    if (!routes || routes.length === 0) {
      return [];
    }

    const route = routes[0];
    const data = route?.sections || [];
    if (data.length === 0) {
      return [];
    }

    const getPolylineArray = data.map((item: any) => {
      try {
        return {
          shape: decode(item.polyline),
        };
      } catch (error) {
        return { shape: { polyline: [] } };
      }
    });

    let ArrayOfShape: any[] = [];
    getPolylineArray.forEach((item: any) => {
      if (item.shape?.polyline && Array.isArray(item.shape.polyline)) {
        ArrayOfShape.push(item.shape.polyline);
      }
    });

    if (ArrayOfShape.length === 0) {
      return [];
    }

    const concatArrays = ArrayOfShape.flat();
    const getPoints = concatArrays
      .map((value) => {
        if (Array.isArray(value) && value.length >= 2) {
          return {
            lat: value[0].toString(),
            lng: value[1].toString(),
          };
        }
        return null;
      })
      .filter((point): point is IRoutePoint => point !== null);

    return getPoints;
  }, []);

  const getLastSuggestion = useCallback((suggestedRouteData: any) => {
    if (Array.isArray(suggestedRouteData) && suggestedRouteData.length > 0) {
      return suggestedRouteData[suggestedRouteData.length - 1];
    }

    if (suggestedRouteData?.result && suggestedRouteData.result.length > 0) {
      return suggestedRouteData.result[suggestedRouteData.result.length - 1];
    }

    return null;
  }, []);

  const processRouteSegments = useCallback(
    (routes: any[]) => {
      const suggestedRoutePoints = processSuggestedRoute(routes);
      setSuggestedSegments(suggestedRoutePoints);

      if (suggestedRoutePoints.length > 0) {
        const values = getBoundingBox(suggestedRoutePoints);
        const paddedValues = {
          top: values.top + 0.1,
          bottom: values.bottom - 0.02,
          left: values.left - 0.1,
          right: values.right + 0.1,
        };
        setBoundingBox(paddedValues);
      }
    },
    [processSuggestedRoute]
  );

  const processValidSuggestion = useCallback(
    (lastSuggestion: any, tripId: string) => {
      const suggestedDataWithTripId = {
        ...lastSuggestion,
        tripId: tripId,
      };
      setSuggestedRouteData(suggestedDataWithTripId);

      const routes = lastSuggestion.completed_route?.routes;
      if (routes && routes.length > 0) {
        processRouteSegments(routes);
      }
    },
    [processRouteSegments]
  );

  const processSuggestedRouteData = useCallback(
    async (tripId: string) => {
      try {
        // Então por consistência, sempre consultamos a sugestão de rota no backend, já que ele mantém o estado real
        // da rota e suas sugestões
        const suggestedRouteToUse: any = await getSuggestedRoute(tripId);

        if (!suggestedRouteToUse) {
          handleClearSuggest();
          return;
        }

        const lastSuggestion = getLastSuggestion(suggestedRouteToUse);

        if (lastSuggestion) {
          processValidSuggestion(lastSuggestion, tripId);
        } else {
          handleClearSuggest();
        }
      } catch (error) {
        console.error("[processSuggestedRouteData]:: ERROR - failed to process suggested route:", error);
      }
    },
    [getLastSuggestion, handleClearSuggest, processValidSuggestion]
  );

  const fetchRouteDetails = useCallback(
    async (skipSuggestedRoute = false) => {
      // Usa refs para evitar loops - não depende de selected no useCallback
      const routeId = selectedRouteIdRef.current;
      const tripId = selectedTripIdRef.current;

      if (!routeId || isFetchingRouteDetailsRef.current) {
        return;
      }

      isFetchingRouteDetailsRef.current = true;

      try {
        const response = await fetchDetail(routeId);
        // Paradas programadas (detalhadas, com coordenadas)
        const plannedStopsDetailed = response.data.result.stops.map((stop: any) => ({
          id: stop.routeStopId ?? stop.id,
          stopId: stop.stopId,
          name: stop.stopName ?? stop.stop?.note,
          category: stop.category ?? stop.stop?.category,
          order: stop.stopQueueOrder,
          position: {
            lat: stop.position?.lat ?? stop.stop?.position?.lat,
            lng: stop.position?.lng ?? stop.stop?.position?.long,
            label: stop.position?.address?.label ?? stop.stop?.position?.address?.label ?? "",
          },
          checkpointId: stop.checkpointId,
        }));

        // Array simplificado para inputs (programadas)
        const plannedStopsForInputs = plannedStopsDetailed.map((stop: any) => ({
          note: stop.name ?? "Parada programada",
          label: stop.position.label,
          checkpointId: stop.checkpointId,
          order: stop.order,
        }));

        // Buscar paradas não programadas para a viagem selecionada
        let unplannedStopsForInputs: Array<{
          note: string;
          label: string;
          checkpointId?: string;
          order?: number;
        }> = [];
        let unplannedMarkers: ISandboxMarker[] = [];

        if (routeId && tripId) {
          try {
            const unplannedResponse = await fetchUnplannedStops(routeId, tripId);
            const unplannedRaw = unplannedResponse?.data?.result ?? unplannedResponse?.result ?? [];

            const normalizeStopType = (stop: any): string | undefined => {
              const raw = stop?.type ?? stop?.stopType ?? stop?.stop_type ?? stop?.routeStopType;
              return typeof raw === "string" ? raw.toUpperCase() : undefined;
            };

            const unplannedRawFiltered = (unplannedRaw || []).filter((stop: any) => {
              const t = normalizeStopType(stop);
              // Se o backend enviar `type`, validamos; se não enviar, assumimos que o endpoint já é de unplanned.
              return t ? t === "UNPLANNED" : true;
            });

            unplannedStopsForInputs = unplannedRawFiltered.map((stop: any, index: number) => {
              const lat = stop.position?.lat;
              const lng = stop.position?.lng;
              const label =
                stop.position?.address?.label ??
                stop.position?.label ??
                stop.stopName ??
                (lat != null && lng != null ? `${lat}, ${lng}` : "Parada não programada");

              const isUnplanned = normalizeStopType(stop) === "UNPLANNED";
              return {
                note: isUnplanned ? `${stop.stopName ?? "Parada"} (Não programada)` : (stop.stopName ?? "Parada"),
                label,
                checkpointId: stop.checkpointId ?? undefined,
                order: stop.stopQueueOrder ?? plannedStopsForInputs.length + index + 1,
              };
            });

            // Markers para as paradas não programadas no mapa
            unplannedMarkers = unplannedRawFiltered
              .filter((stop: any) => stop.position?.lat != null && stop.position?.lng != null)
              .map((stop: any, index: number) => ({
                id: `unplanned-${stop.routeStopId ?? stop.stopId ?? index}`,
                markerProps: {
                  iconNames: ["rioglyph rioglyph-map-marker"],
                  name: stop.stopName ?? "Parada não programada",
                  markerColor: "bg-map-marker-danger",
                  active: false,
                  fixed: false,
                },
                position: {
                  lat: stop.position.lat,
                  lng: stop.position.lng,
                },
              }));
          } catch (error) {
            // Caso a API de paradas não programadas falhe, apenas loga e segue com as programadas
            console.error("Erro ao buscar paradas não programadas:", error);
          }
        }

        if (!skipSuggestedRoute && tripId) {
          await processSuggestedRouteData(tripId);
        }
        setDetails(response.data.result);
        // Combina paradas programadas e não programadas para exibição em inputs,
        // ordenando por stopQueueOrder quando disponível.
        const combinedStopsForInputs = [...plannedStopsForInputs, ...unplannedStopsForInputs].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );
        setStops(combinedStopsForInputs);

        const {
          zoom,
          markers: plannedMarkers,
          position,
          segments,
        } = handleRoutes(response.data.result.responseHere.routes, plannedStopsDetailed);

        setSegments(segments);
        setPosition(position);
        // Adiciona markers das paradas não programadas junto com os markers já existentes
        setMarkers([...plannedMarkers, ...unplannedMarkers]);
        setZoom(zoom);
      } catch (error) {
        console.error(error);
      } finally {
        isFetchingRouteDetailsRef.current = false;
      }
    },
    [handleRoutes, processSuggestedRouteData]
  ); // Removido selected - usa refs

  const handleAcceptTrip = async (tripId: string, accepted: boolean) => {
    setIsLoadingAcceptTrip(true);
    try {
      await acceptTrip({ tripId, accepted });
      await handleClearSuggest();
      await fetchRouteDetails(true); // skipSuggestedRoute = true
      await fetchAllTrips();
      Notification.success("Resposta enviada com sucesso");
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Notification.error(error?.response?.data?.message);
      } else {
        Notification.error("Não foi possível aceitar a viagem.");
      }
    } finally {
      setIsLoadingAcceptTrip(false);
    }
  };

  const handleClear = () => {
    // Limpa interval se existir
    if (detailsIntervalRef.current) {
      clearInterval(detailsIntervalRef.current);
      detailsIntervalRef.current = null;
    }
    // Limpa refs
    selectedTripIdRef.current = null;
    selectedRouteIdRef.current = null;
    // Limpa estados
    setSelected(null);
    setDetails(null);
    setMarkers([]);
    setSegments([]);
    setCurrencySegments([]);
    setSuggestedSegments([]);
    setSuggestedRouteData(null);
    setPosition(positionDefault);
    setIsPolyline(false);
    setUrgentAlerts([]);
    setUrgentAlertMarkers([]);
  };

  const loadUrgentAlerts = useCallback(async (tripId: string) => {
    try {
      const response = await fetchUrgentAlerts(tripId);
      const result = response?.data?.result ?? response?.result ?? response;
      const rawLocations = result?.locations ?? (Array.isArray(result) ? result : []);

      if (!Array.isArray(rawLocations)) {
        setUrgentAlerts([]);
        setUrgentAlertMarkers([]);
        return;
      }

      const routeId = selectedRouteIdRef.current ?? result?.routeId ?? "";
      const disarmedAt = result?.disarmedAt;
      const alerts: IUrgentAlert[] = rawLocations.map((loc: any) => ({
        ...loc,
        tripId: loc.tripId ?? result?.tripId ?? tripId,
        routeId: loc.routeId ?? routeId,
        disarmedAt: loc.disarmedAt ?? disarmedAt,
      }));

      setUrgentAlerts(alerts);

      const alertMarkers: ISandboxMarker[] = alerts
        .filter((alert) => alert.latitude && alert.longitude)
        .map((alert, index) => ({
          id: `urgent-alert-${alert.id ?? index}`,
          markerProps: {
            iconNames: ["rioglyph rioglyph-warning-sign text-color-white"],
            name: `Alerta urgente`,
            markerColor: "bg-map-marker-danger",
            active: false,
            fixed: false,
          },
          position: {
            lat: alert.latitude,
            lng: alert.longitude,
          },
        }));

      setUrgentAlertMarkers(alertMarkers);
    } catch (error) {
      console.error("Erro ao buscar alertas urgentes:", error);
      setUrgentAlerts([]);
      setUrgentAlertMarkers([]);
    }
  }, []);

  const handleSelectFilterByType = (type: string) => {
    setPage(1);
    setSize(PAGE_SIZE);
    handleClear();
    setFilterByType((prev) => (prev === type ? "" : type));
  };

  const send = useCallback(
    (positions: Position[]) => {
      const lastLocation = positions[positions.length - 1];
      if (lastLocation) {
        setLocationNow(`${handleSimpleLocation(lastLocation?.lat)},${handleSimpleLocation(lastLocation.lng)}`);
        setMarkerNow(
          handleGenerateMarker({
            lat: lastLocation?.lat,
            lng: lastLocation?.lng,
            mode: "street_truck",
          })
        );
        setPosition({
          lat: lastLocation?.lat,
          lng: lastLocation?.lng,
        });
      }
      setCurrencySegments(positions);
    },
    [handleGenerateMarker, handleSimpleLocation]
  );

  const handleDetails = (select: IOnGoingTrip) => {
    setSelected(select);
    ctx?.sendMessage(select.tripId);
    ctx?.handleSaveTrip(select.tripId);
    setView("DETAILS");
    setClosed(false);
  };

  useEffect(() => {
    const notificationTripId = (location.state as any)?.notificationTripId;
    if (!notificationTripId || isLoading) return;

    navigate(location.pathname, { replace: true, state: {} });

    const openTripFromNotification = async () => {
      const existingTrip = routes.find(r => r.tripId === notificationTripId);
      if (existingTrip) {
        handleDetails(existingTrip);
        return;
      }

      try {
        const response = await fetchDetailsTripOnGoing(notificationTripId);
        if (response.data?.result) {
          handleDetails(response.data.result as IOnGoingTrip);
        }
      } catch (error) {
        Notification.error("Não foi possível abrir os detalhes da viagem.");
      }
    };

    openTripFromNotification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, isLoading]);

  const handleShare = async (id: string) => {
    try {
      // if (!isPolyline || !locationNow) return;
      const response = await fetchUrltripShare(id);
      window.open(`${config.publicRoute}?link=${response.data.result}`, "_blank");
    } catch (error) {}
  };

  const Update = useCallback(async () => {
    // Usa ref para evitar loops - não depende de selected no useCallback
    const tripId = selectedTripIdRef.current;
    if (!tripId || isUpdatingDetailsRef.current) {
      return;
    }

    isUpdatingDetailsRef.current = true;

    try {
      const response = await fetchDetailsTripOnGoing(tripId);

      if (response.data.result) {
        setStatusRouteNow(response.data.result.routeStatusNow);
        setStatusTripNow(response.data.result.tripStatusNow);
        setProgressNow(response.data.result.actualProgress);
        setIsPolyline(false);

        // Atualiza selected apenas com dados complementares, sem mudar tripId
        setSelected((prev) => {
          if (prev?.tripId === tripId) {
            return { ...prev, ...response.data.result };
          }
          return prev;
        });

        if (response.data.result.positions?.length) {
          send(response.data.result.positions?.reverse());
        }
      }
    } finally {
      isUpdatingDetailsRef.current = false;
    }
  }, [send]); // Removido selected?.tripId - usa ref

  // useEffect para carregar detalhes quando selected muda
  // Usa refs para evitar loops - apenas selected?.routeId e selected?.tripId como dependências
  useEffect(() => {
    // Limpa interval anterior se existir
    if (detailsIntervalRef.current) {
      clearInterval(detailsIntervalRef.current);
      detailsIntervalRef.current = null;
    }

    // Atualiza refs com os valores atuais
    selectedRouteIdRef.current = selected?.routeId || null;
    selectedTripIdRef.current = selected?.tripId || null;

    if (selected?.routeId && selected?.tripId) {
      // Carrega dados iniciais
      fetchRouteDetails();
      Update();
      loadUrgentAlerts(selected.tripId);

      // Configura interval para atualizações periódicas (30 segundos)
      detailsIntervalRef.current = setInterval(() => {
        // Verifica se ainda há uma viagem selecionada
        if (!selectedTripIdRef.current || !selectedRouteIdRef.current) {
          return;
        }
        // Evita consultar enquanto aceita/rejeita sugestão
        if (!isLoadingAcceptTrip) {
          Update();
          if (selectedTripIdRef.current) {
            processSuggestedRouteData(selectedTripIdRef.current);
            loadUrgentAlerts(selectedTripIdRef.current);
          }
        }
      }, 30000);
    }

    // Cleanup: limpa interval quando selected muda ou componente desmonta
    return () => {
      if (detailsIntervalRef.current) {
        clearInterval(detailsIntervalRef.current);
        detailsIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.routeId, selected?.tripId]); // Apenas IDs como dependências - funções usam refs internamente e são estáveis

  const handleSort = (dir: string, field: string) => {
    setSort({ [field]: dir });
    setFieldSort(field);
  };

  const handlePage = async (config: ConfigPaginator) => {
    setIsLoadingMore(true);
    setSize(config.next);
  };

  const handleResetPage = () => {
    setSize(PAGE_SIZE);
  };

  const handleSearchValueChange = (text: string) => {
    setSearchValue(text);
  };

  const handleFinishTrip = async (tripId: string) => {
    try {
      await finishTripManually(tripId);
      Notification.success("Viagem finalizada com sucesso");
      handleCloseSidebar();
      await fetchAllTrips();
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Notification.error(error.response.data.message);
      } else {
        Notification.error("Não foi possível finalizar a viagem.");
      }
    }
  };

  const handleCloseSidebar = () => {
    // Limpa interval e refs para parar requisições
    if (detailsIntervalRef.current) {
      clearInterval(detailsIntervalRef.current);
      detailsIntervalRef.current = null;
    }
    selectedTripIdRef.current = null;
    selectedRouteIdRef.current = null;
    // Limpa selected para parar o useEffect de detalhes
    setSelected(null);
    setClosed(true);
    setView("DEFAULT");
    fetchAllTrips();
  };

  return {
    routes,
    followers,
    searchValue,
    sort,
    fieldSort,
    selected,
    details,
    page,
    pages,
    isPolyline,
    dashboard,
    filterByType,
    position,
    boundingBox,
    segments,
    markers,
    view,
    closed,
    stops,
    currencySegments,
    suggestedSegments,
    suggestedRouteData,
    statusTripNow,
    statusRouteNow,
    progressNow,
    markerNow,
    locationNow,
    checkpoint,
    openStages,
    size,
    totalTrips,
    handleSelectFilterByType,
    handleDetails,
    handleShare,
    handleSort,
    handlePage,
    handleSearchValueChange,
    handleCloseSidebar,
    setSearchValue,
    loadCheckpointDetails,
    setOpenStages,
    setClosed,
    fetchRouteDetails,
    handleAcceptTrip,
    handleFinishTrip,
    handleResetPage,
    PAGE_SIZE,
    suggestedRoutes,
    rioDrivers,
    isLoading,
    isLoadingMore,
    isLoadingAcceptTrip,
    zoom,
    urgentAlerts,
    urgentAlertMarkers,
    loadUrgentAlerts,
  };
};
